type RejectionLike = {
  reason: unknown;
  preventDefault: () => void;
};

type RejectionTarget = {
  addEventListener: (type: "unhandledrejection", listener: (event: RejectionLike) => void) => void;
};

type LoggerLike = {
  warn: (...args: unknown[]) => void;
};

/**
 * PGlite's `relaxedDurability: true` mode schedules `syncToFs` as fire-and-forget after
 * every write, including the post-initdb sync inside `PGlite.create`. When the underlying
 * IDBFS rejects — most visibly in Electron renderers on first boot with ErrnoError errno
 * 44 (ENOENT) and a "<generic error, no stack>" stack — the rejection escapes as an
 * "Uncaught (in promise)" warning. The in-memory FS still holds the data and PGlite
 * re-syncs after every subsequent write, so the rejection is benign. WEB-2017.
 */
export function isPGliteFilesystemRejection(reason: unknown): boolean {
  if (!reason || typeof reason !== "object") return false;
  const candidate = reason as { name?: unknown };
  return candidate.name === "ErrnoError";
}

/**
 * Wire an `unhandledrejection` listener on `target` that swallows PGlite IDBFS rejections
 * — see `isPGliteFilesystemRejection` for the rationale — and surfaces them as warnings on
 * `logger`. Other rejections pass through untouched. WEB-2017.
 */
export function installPGliteFsRejectionSuppression(
  target: RejectionTarget,
  logger: LoggerLike,
  context: string,
): void {
  target.addEventListener("unhandledrejection", (event) => {
    if (!isPGliteFilesystemRejection(event.reason)) return;
    event.preventDefault();
    const reason = event.reason as { name?: unknown; errno?: unknown; message?: unknown };
    logger.warn(`[${context}] Suppressed unhandled PGlite filesystem rejection`, {
      name: reason.name,
      errno: reason.errno,
      message: reason.message,
    });
  });
}
